import {
  type ForceUpdatePolicy,
  ForceUpdatePolicySchema,
  POLICY_EVALUATION,
  type PolicyEvaluation,
  type PolicyEvaluationContext,
} from '@/tauri/force-update/forceUpdatePolicy';
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { isVersionBelowMinimum, isVersionBlocked } from './versionComparison';

const POLICY_CONFIG_KEY = 'desktop_force_update_policy';

const policyCache: { data: unknown; timestamp: number } = {
  data: null,
  timestamp: 0,
};

/**
 * Fetch force update policy from the Firebase Remote Config parameter
 * `desktop_force_update_policy`. Every failure path — missing entry, malformed
 * payload, suppressed telemetry, fetch error — resolves to null, which callers
 * treat as "no enforcement".
 */
export async function fetchPolicyFromRemoteConfig(): Promise<ForceUpdatePolicy | null> {
  // TELEMETRY-GATE: load-site gate — short-circuit before the dynamic import so
  // the Firebase SDK is never fetched or evaluated on a suppressed build.
  if (!isSaasTelemetryEnabled) return null;
  try {
    // Dynamic import keeps the Firebase SDK out of the boot chunk; the
    // Chronicle firmware catalog loads @/lib/firebase the same way.
    const { fetchRemoteConfigValue } = await import('@/lib/firebase');
    const payload = await fetchRemoteConfigValue<unknown>(POLICY_CONFIG_KEY, policyCache);

    if (!payload) {
      logger.info('Force update policy is not configured in Remote Config');
      return null;
    }

    const parseResult = ForceUpdatePolicySchema.safeParse(payload);
    if (!parseResult.success) {
      logger.error('Invalid force update policy payload from Remote Config', {
        error: parseResult.error,
        payload,
      });
      return null;
    }

    logger.info('Successfully fetched force update policy', { ...parseResult.data });
    return parseResult.data;
  } catch (error) {
    logger.error('Error fetching force update policy from Remote Config', { error });
    return null;
  }
}

/**
 * Placeholder / internal-testing desktop builds report version `0.0.0`
 * (misconfigured release artifacts, e2e builds). Semver treats that as below
 * any real min_supported_version, which traps testers on the force-update
 * screen with no installable update. Skip enforcement for this sentinel.
 * @see DSK-1225
 */
const isInternalTestingVersion = (version: string): boolean => version === '0.0.0';

/**
 * Evaluate force update policy against current version and state
 * @param context - Policy evaluation context with current version, policy, state, and recording status
 * @returns PolicyEvaluation with result and metadata
 */
export function evaluatePolicy(context: PolicyEvaluationContext): PolicyEvaluation {
  const { currentVersion, policy, isRecording } = context;
  const deadlineAt = new Date(policy.force_update_deadline);

  // STEP 0: Internal-testing / placeholder builds (DSK-1225)
  if (isInternalTestingVersion(currentVersion)) {
    logger.info('Skipping force update for internal-testing version 0.0.0', {
      currentVersion,
    });
    return {
      result: POLICY_EVALUATION.OK,
    };
  }

  // STEP 1: Check if current version is in blocked list (immediate FORCED)
  if (policy.blocked_versions && policy.blocked_versions.length > 0) {
    if (isVersionBlocked(currentVersion, policy.blocked_versions)) {
      logger.warn('Current version is in blocked list', {
        currentVersion,
        blockedVersions: policy.blocked_versions,
      });
      const forced: PolicyEvaluation = {
        result: POLICY_EVALUATION.FORCED,
        reason: 'blocked_version',
      };
      // Recording override handled after base evaluation
      if (isRecording) {
        logger.info('Recording active; downgrading FORCED to GRACE (blocked_version)', {
          currentVersion,
        });
        return {
          result: POLICY_EVALUATION.GRACE,
          reason: 'grace_period_active',
          deadlineAt,
        };
      }
      return forced;
    }
  }

  // STEP 2: Check if current version is below minimum supported version
  if (isVersionBelowMinimum(currentVersion, policy.min_supported_version)) {
    const now = new Date();
    const isPastDeadline = now >= deadlineAt;

    if (isPastDeadline) {
      logger.warn('Absolute deadline reached; forcing update', {
        currentVersion,
        minSupportedVersion: policy.min_supported_version,
        deadlineAt: deadlineAt.toISOString(),
      });
      if (isRecording) {
        logger.info(
          'Recording active; downgrading FORCED to GRACE (min_version_expired)',
          {
            currentVersion,
          }
        );
        return {
          result: POLICY_EVALUATION.GRACE,
          reason: 'grace_period_active',
          deadlineAt,
        };
      }
      return {
        result: POLICY_EVALUATION.FORCED,
        reason: 'min_version_expired',
        deadlineAt,
      };
    }

    // Deadline in the future → GRACE until deadline
    logger.info('Below minimum; grace until deadline', {
      currentVersion,
      minSupportedVersion: policy.min_supported_version,
      deadlineAt: deadlineAt.toISOString(),
    });
    return {
      result: POLICY_EVALUATION.GRACE,
      reason: 'grace_period_active',
      deadlineAt,
    };
  }

  // STEP 3: Version is compliant
  logger.info('Current version is compliant', {
    currentVersion,
    minSupportedVersion: policy.min_supported_version,
  });
  return {
    result: POLICY_EVALUATION.OK,
  };
}
